
import { useMemo, useState, useEffect, useCallback } from "react"
import { useChildStore } from "@/lib/store/child"
import { logger } from "@/lib/services/logger-service"
import { CompactSelector } from "./CompactSelector"
import { FullSelector } from "./FullSelector"
import { useChildrenData } from "./useChildrenData"
import { ChildSelectorProps, ChildDropdownItem } from "./types"

export function ChildSelector({
  children: providedChildren,
  selectedChild: providedSelectedChild,
  onChildSelect,
  onAddChild,
  onChildChange,
  childId,
  selectClassName,
  showAddButton = true,
  connectToStore = true,
  className,
  selectOnly = false
}: ChildSelectorProps) {
  const { children: storeChildren, selectedChild: storeSelectedChild, setSelectedChild } = useChildStore()
  const { loadedChildren, loading, loadingError, fetchChildren } = useChildrenData()
  
  // Determine which children array to use
  const children = useMemo(() => {
    return providedChildren || loadedChildren.length > 0 ? loadedChildren : storeChildren || [];
  }, [providedChildren, loadedChildren, storeChildren]);
  
  // Determine which selected child to use
  const selectedChild = useMemo(() => {
    return providedSelectedChild || (childId ? children.find(c => c.id === childId) : null) || storeSelectedChild;
  }, [providedSelectedChild, childId, children, storeSelectedChild]);

  // Helper function to select the first child
  const selectFirstChild = useCallback((childrenToUse = children) => {
    if (childrenToUse.length === 0) return
    
    const firstChild = childrenToUse[0]
    
    // Avoid unnecessary updates by checking if we're already selecting this child
    if (selectedChild?.id === firstChild.id) return
    
    logger.debug('Selecting first child', {
      component: 'ChildSelector',
      childId: firstChild.id,
      childName: firstChild.name
    })
    
    if (onChildChange) {
      onChildChange(firstChild.id)
    }
    
    if (onChildSelect) {
      onChildSelect(firstChild)
    }
    
    // Also update the child store if connected
    if (connectToStore) {
      setSelectedChild({
        id: firstChild.id,
        name: firstChild.name,
        date_of_birth: firstChild.date_of_birth || '',
      })
    }
  }, [children, selectedChild?.id, onChildChange, onChildSelect, connectToStore, setSelectedChild])

  // Load children if not provided
  useEffect(() => {
    if (!providedChildren && loadedChildren.length === 0 && storeChildren.length === 0 && !loading) {
      fetchChildren()
    }
  }, [providedChildren, storeChildren.length, loadedChildren.length, loading, fetchChildren])

  // Ensure a child is selected whenever children array changes
  useEffect(() => {
    // If we have children but no selection, select the first child
    if (children.length > 0 && !selectedChild) {
      logger.debug('No child selected and children available, selecting first child', {
        component: 'ChildSelector',
        childrenCount: children.length
      })
      selectFirstChild()
    }
  }, [children.length, selectedChild, selectFirstChild])

  // Handle change from the Select component
  const handleChange = useCallback((childId: string) => {
    const child = children.find(c => c.id === childId)
    
    if (!child) {
      logger.warn('Child not found for id', {
        component: 'ChildSelector',
        childId
      })
      return
    }
    
    logger.debug('Child selected from dropdown', {
      component: 'ChildSelector',
      childId: child.id,
      childName: child.name
    })
    
    if (onChildSelect) {
      onChildSelect(child)
    }
    
    if (onChildChange) {
      onChildChange(childId)
    }
    
    // Also update the child store if connected
    if (connectToStore) {
      setSelectedChild({
        id: child.id,
        name: child.name,
        date_of_birth: child.date_of_birth || '',
      })
    }
  }, [children, onChildSelect, onChildChange, connectToStore, setSelectedChild])

  // Determine the current selected value
  const selectedValue = childId || selectedChild?.id || ''

  // Render only the Select component
  if (selectOnly) {
    return (
      <CompactSelector 
        selectedValue={selectedValue}
        handleChange={handleChange}
        loading={loading}
        children={children}
        selectClassName={selectClassName}
        loadingError={loadingError}
        showAddButton={showAddButton}
        onAddChild={onAddChild}
      />
    )
  }

  // Full component with motion and add button
  return (
    <FullSelector
      selectedValue={selectedValue}
      handleChange={handleChange}
      loading={loading}
      children={children}
      selectClassName={selectClassName}
      loadingError={loadingError}
      showAddButton={showAddButton}
      onAddChild={onAddChild}
      className={className}
    />
  )
}

// Export types for external usage
export type { ChildDropdownItem, ChildSelectorProps };
